import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ConfirmDialogService, DialogConfig } from './confirm-dialog.service';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @for (dialog of dialogs; track dialog.id) {
      <div class="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" (click)="cancel(dialog)"></div>
        <div class="relative bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">
          <h2 class="text-lg font-bold text-text">{{ dialog.header }}</h2>
          <p *ngIf="dialog.message" class="text-sm text-text-secondary leading-relaxed">{{ dialog.message }}</p>
          <div *ngIf="dialog.details" class="rounded-lg bg-surface border border-border px-3 py-2 text-sm text-text-secondary whitespace-pre-wrap max-h-40 overflow-y-auto">{{ dialog.details }}</div>
          @if (dialog.inputs && dialog.inputs.length > 0) {
            @for (input of dialog.inputs; track input.name) {
              @if (input.type === 'textarea') {
                <textarea
                  class="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary-500 resize-none"
                  rows="3"
                  [placeholder]="input.placeholder ?? ''"
                  [(ngModel)]="inputValues[input.name]"
                ></textarea>
              } @else {
                <input
                  type="text"
                  class="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary-500"
                  [placeholder]="input.placeholder ?? ''"
                  [(ngModel)]="inputValues[input.name]"
                />
              }
            }
          }
          <div class="flex justify-end gap-3 mt-2">
            <button
              *ngIf="dialog.cancelText"
              type="button"
              (click)="cancel(dialog)"
              class="rounded-lg border border-border text-text px-5 py-2 text-sm font-medium hover:bg-surface transition-colors cursor-pointer"
            >{{ dialog.cancelText }}</button>
            <button
              type="button"
              (click)="confirm(dialog)"
              class="rounded-lg border border-secondary-500 text-secondary-500 bg-transparent px-5 py-2 text-sm font-medium hover:bg-secondary-500/10 transition-colors cursor-pointer"
            >{{ dialog.confirmText }}</button>
          </div>
        </div>
      </div>
    }
  `,
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  dialogs: DialogConfig[] = [];
  inputValues: Record<string, string> = {};
  private subs: Subscription[] = [];

  constructor(private dialogService: ConfirmDialogService) {}

  @HostListener('document:keydown.escape')
  onEscape() {
    const last = this.dialogs[this.dialogs.length - 1];
    if (last) this.cancel(last);
  }

  ngOnInit() {
    this.subs.push(
      this.dialogService.open$.subscribe(dialog => {
        if (dialog.inputs) {
          dialog.inputs.forEach(i => { this.inputValues[i.name] = i.value ?? ''; });
        }
        this.dialogs = [...this.dialogs, dialog];
      }),
      this.dialogService.close$.subscribe(id => {
        this.dialogs = this.dialogs.filter(d => d.id !== id);
      }),
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  cancel(dialog: DialogConfig) {
    dialog.resolve(dialog.inputs ? null : false);
    this.dialogService.close(dialog.id);
  }

  confirm(dialog: DialogConfig) {
    if (dialog.inputs) {
      const values: Record<string, string> = {};
      dialog.inputs.forEach(i => { values[i.name] = this.inputValues[i.name] ?? ''; });
      dialog.resolve(values);
    } else {
      dialog.resolve(true);
    }
    this.dialogService.close(dialog.id);
  }
}
